<script setup lang="ts">
/**
 * "แนะนำให้ตอบ" — the personal reply script in the draft box (owner-approved mockup 17 ต.ค. 69, LeanNew / LeanReturning).
 * The message with the values the system filled in highlighted and the blanks the staff must fill as dashed chips;
 * "ใส่ในช่องพิมพ์" moves it into the typing box (never sends) · "ปรับตามแนวลูกค้า · <แนว>" = today's AI draft, on press
 * only, re-toning it for the customer's style (numbers / dates / links kept — api/app/ai/style_adapt.py) ·
 * "ข้าม" hides it for this customer message · "(i)" why · "ถัดไป" the other suggestions (an apology is never first).
 * "ให้ AI พาไปขั้นต่อไป" (คู่มือปิดการขาย, owner 18 ต.ค. 69): on press only, the AI answers what the customer said and asks
 * at most one question toward the chat's step now (stores/script.ts nextStep) — for when the owner's answer does not fit.
 * Rendered by DraftBox when stores/script.ts `showing()`; the inserting itself is the composer's (`insert`).
 * Every suggestion is the owner's own answer (owner 17 ต.ค. 69): the questions it could not answer because the owner has
 * no answer switched on for them are named under it (UndefinedAnswers) — the system never writes their text.
 */
import { computed, ref, watch } from 'vue'
import { NEXT_STEP_TITLE, kindLabel, partsOf, sourceLabel, styleText } from '../../lib/script'
import { useInbox } from '../../stores/inbox'
import { useScript } from '../../stores/script'
import Icon from '../ui/Icon.vue'
import UndefinedAnswers from './UndefinedAnswers.vue'

const props = defineProps<{ convId: string; canRephrase: boolean }>()
const emit = defineEmits<{ insert: [mode: 'replace' | 'append'] }>()
const store = useInbox()
const script = useScript()

const list = computed(() => script.suggestions(props.convId))
const s = computed(() => script.current(props.convId))
const index = computed(() => (s.value ? list.value.findIndex((x) => x.id === s.value!.id) : -1))
const nextOne = computed(() => (list.value.length > 1 ? list.value[(index.value + 1) % list.value.length] : null))
const parts = computed(() => (s.value ? partsOf(s.value) : []))
const gaps = computed(() => parts.value.filter((p) => p.t === 'gap').length)
const undef = computed(() => script.undefinedOf(props.convId))
/** "ปรับตามแนวลูกค้า · ห้วน ตอบสั้น": the style the AI re-tones for (สุภาพ กระชับ while the script has none). */
const style = computed(() => script.styleOf(props.convId))
const adaptTitle = computed(
  () =>
    'ให้ AI ปรับคำแนะนำนี้ให้เข้ากับแนวลูกค้า ตัวเลข วันที่ และลิงก์คงเดิม (ใช้ค่า AI ต่อครั้ง)' +
    (style.value.known ? '' : ' · ยังไม่รู้แนวลูกค้า ใช้แบบสุภาพ กระชับ'),
)

const typed = computed(() => (store.composers[props.convId]?.text ?? '').trim() !== '')
/** The staff's own words in the typing box (an untouched draft / suggestion taken before is simply replaced). */
const ownWords = computed(() => {
  const st = store.composers[props.convId]
  return typed.value && !((st?.origin === 'ai' || st?.origin === 'script') && !st.edited)
})

const whyOpen = ref(false)
const confirming = ref(false)
watch(
  () => [props.convId, s.value?.id] as const,
  () => {
    whyOpen.value = false
    confirming.value = false
  },
)
const whyId = computed(() => `sg-why-${props.convId}`)

function insert(): void {
  if (ownWords.value) {
    confirming.value = true
    return
  }
  emit('insert', 'replace')
}

function pick(mode: 'replace' | 'append'): void {
  confirming.value = false
  emit('insert', mode)
}
</script>

<template>
  <div v-if="s" class="sg" role="region" aria-label="แนะนำให้ตอบ">
    <div class="sg-h">
      <span class="sg-chip"><Icon name="sparkles" />แนะนำให้ตอบ</span>
      <span v-if="index > 0" class="sg-alt">{{ s.kind === 'apology' ? 'ทางเลือก · ขอโทษ' : `ทางเลือก · ${kindLabel(s.kind)}` }}</span>
      <span class="sg-title">{{ s.title }}</span>
      <span v-if="list.length > 1" class="sg-n mono" :aria-label="`คำแนะนำที่ ${index + 1} จาก ${list.length}`">{{ index + 1 }}/{{ list.length }}</span>
      <button
        v-if="s.why.length"
        type="button"
        class="sg-i"
        :class="{ on: whyOpen }"
        :aria-expanded="whyOpen"
        :aria-controls="whyId"
        aria-label="ทำไมแนะนำแบบนี้"
        title="ทำไมแนะนำแบบนี้"
        @click="whyOpen = !whyOpen"
      >
        <Icon name="info" />
      </button>
      <button type="button" class="sg-off" title="ปิดกล่องแนะนำให้ตอบทุกแชท (เฉพาะคุณ) · เปิดกลับได้ที่แถบเดียวกันนี้" @click="script.setSuggestOff(true)">
        ปิดคำแนะนำ
      </button>
    </div>

    <p class="sg-t" tabindex="0" aria-label="ข้อความที่แนะนำ">
      <template v-for="(p, i) in parts" :key="i"
        ><mark v-if="p.t === 'fact'" class="sg-fact" :title="sourceLabel(p.source) ? `ข้อมูลจาก${sourceLabel(p.source)}` : undefined">{{ p.text }}</mark
        ><span v-else-if="p.t === 'gap'" class="sg-gap"><span class="sr">ต้องเติม: </span>{{ p.label }}</span
        ><template v-else>{{ p.text }}</template></template
      >
    </p>
    <p v-if="gaps" class="sg-note"><Icon name="pencil" />มีช่องเส้นประ {{ gaps }} ช่อง ระบบไม่มีข้อมูล เติมในช่องพิมพ์ก่อนส่ง</p>
    <UndefinedAnswers v-if="undef.length" compact :topics="undef" />

    <ul v-if="whyOpen" :id="whyId" class="sg-why" aria-label="ทำไมแนะนำแบบนี้">
      <li v-for="(w, i) in s.why" :key="i">
        <span>{{ w.text }}</span><span v-if="sourceLabel(w.source)" class="sg-src">{{ sourceLabel(w.source) }}</span>
      </li>
    </ul>

    <div v-if="confirming" class="sg-a ask" role="group" aria-label="ใส่คำแนะนำกับข้อความที่พิมพ์ไว้">
      <span class="sg-q">มีข้อความที่พิมพ์ไว้แล้ว</span>
      <button type="button" class="sg-b pri" @click="pick('append')">ต่อท้าย</button>
      <button type="button" class="sg-b" @click="pick('replace')">แทนที่</button>
      <button type="button" class="sg-b ghost" @click="confirming = false">ยกเลิก</button>
    </div>
    <div v-else class="sg-a">
      <button type="button" class="sg-b pri" @click="insert"><Icon name="arrowDown" />ใส่ในช่องพิมพ์</button>
      <button v-if="canRephrase" type="button" class="sg-b" :title="adaptTitle" @click="script.adapt(convId)">
        <Icon name="sparkles" />ปรับตามแนวลูกค้า<span class="sg-style"> · {{ styleText(style) }}</span>
      </button>
      <button v-if="canRephrase" type="button" class="sg-b" :title="NEXT_STEP_TITLE" @click="script.nextStep(convId)">
        <Icon name="sparkles" />ให้ AI พาไปขั้นต่อไป
      </button>
      <button type="button" class="sg-b ghost" title="ซ่อนคำแนะนำของข้อความนี้" @click="script.skip(convId)">ข้าม</button>
      <span class="grow"></span>
      <button v-if="nextOne" type="button" class="sg-next" :aria-label="`คำแนะนำถัดไป: ${nextOne.title}`" @click="script.next(convId)">
        <span class="sg-next-l">ถัดไป:</span><span class="sg-next-t">{{ nextOne.title }}</span><Icon name="chevronRight" />
      </button>
      <span v-else-if="!typed" class="kbd-hint">กด <kbd>Tab</kbd> เพื่อใส่</span>
    </div>
  </div>
</template>

<style scoped>
.sg {
  padding: 10px 12px 12px;
  border-radius: 11px 11px 0 0;
  background: #fbfdfc;
  box-shadow: inset 0 0 0 1.5px var(--ac);
}
.sg-h {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 8px;
  min-width: 0;
}
.sg-chip {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13.5px;
  font-weight: 700;
  line-height: 20px;
  color: var(--ac2);
  white-space: nowrap;
}
.sg-chip :deep(.ic) {
  width: 16px;
  height: 16px;
  color: var(--ac);
}
.sg-alt {
  flex: none;
  height: 20px;
  padding: 0 7px;
  border-radius: 6px;
  background: #eef2f0;
  color: #3e4d46;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}
.sg-title {
  flex: 1 1 200px;
  min-width: 0;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.sg-n {
  flex: none;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.sg-off {
  flex: none;
  height: 24px;
  padding: 0 8px;
  border-radius: 6px;
  color: var(--mut);
  font-size: 12px;
  line-height: 24px;
}
.sg-off:hover {
  background: #eef2f0;
  color: var(--ink);
}
.sg-i {
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--mut);
}
.sg-i:hover,
.sg-i.on {
  background: #eef2f0;
  color: var(--ink);
}
.sg-i :deep(.ic) {
  width: 17px;
  height: 17px;
}
.sg-t {
  margin-top: 6px;
  font-size: 14px;
  line-height: 25px;
  color: var(--ink);
  white-space: pre-wrap; /* the API's paragraphs ("\n\n", owner 17 ต.ค. 69 "ไม่ติดกันเป็นพืด") as the customer will see them */
  overflow-wrap: anywhere;
  max-height: min(240px, 32vh); /* a 5-paragraph answer (9 lines) whole, the question at the end without scrolling */
  overflow-y: auto;
}
.sg-t:focus-visible {
  outline: 2px solid #9fcdb8;
  outline-offset: 2px;
  border-radius: 4px;
}
.sg-fact {
  padding: 1px 5px;
  border-radius: 4px;
  background: #def1ed;
  color: var(--ac2);
  font-weight: 600;
}
.sg-gap {
  display: inline-block;
  padding: 0 7px;
  margin: 0 1px;
  border-radius: 6px;
  border: 1px dashed #b8740a;
  background: #fffaf0;
  color: var(--warn);
  font-size: 13px;
  font-weight: 600;
  line-height: 21px;
}
.sg-note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 12px;
  line-height: 18px;
  color: var(--warn);
}
.sg-note :deep(.ic) {
  flex: none;
  width: 13px;
  height: 13px;
}
.sg-why {
  margin: 8px 0 0;
  padding: 8px 12px;
  list-style: none;
  border-radius: 8px;
  background: #f4f6f5;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sg-why li {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 2px 8px;
  font-size: 12.5px;
  line-height: 19px;
  color: #33423b;
}
.sg-src {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.sg-a {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin-top: 10px;
}
.sg-q {
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  margin-right: 4px;
}
.sg-b {
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #c3cdc8;
  background: #fff;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: var(--ink);
  white-space: nowrap;
}
.sg-b:hover {
  background: #f4f7f5;
}
.sg-b :deep(.ic) {
  width: 14px;
  height: 14px;
  color: var(--ac);
}
.sg-style {
  font-weight: 500;
  color: var(--mut);
}
.sg-b.pri {
  border-color: var(--ac);
  background: var(--ac);
  color: #fff;
}
.sg-b.pri:hover {
  background: var(--ac2);
  border-color: var(--ac2);
}
.sg-b.pri :deep(.ic) {
  color: #fff;
}
.sg-b.ghost {
  border-color: transparent;
  background: transparent;
  color: var(--mut);
  font-weight: 500;
  padding: 0 10px;
}
.sg-b.ghost:hover {
  background: #eef2f0;
  color: var(--ink);
}
.sg-next {
  min-width: 0;
  max-width: 100%;
  min-height: 30px;
  padding: 4px 4px 4px 8px;
  text-align: left;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--ac2);
}
.sg-next:hover {
  background: var(--acs);
}
.sg-next-l {
  flex: none;
  color: var(--mut);
}
.sg-next-t {
  min-width: 0;
  font-weight: 600;
}
.sg-next :deep(.ic) {
  flex: none;
  width: 15px;
  height: 15px;
}
.kbd-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: nowrap;
}
kbd {
  font: 600 12px/18px var(--mono);
  padding: 0 6px;
  border-radius: 5px;
  background: #fff;
  border: 1px solid #d3ddd8;
  border-bottom-width: 2px;
  color: var(--ink);
}
</style>
